import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, Check, ExternalLink } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";

function EventCta({ e, rsvped }) {
  const cls = "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium";
  if (e.registration_mode === "external" && e.registration_url) return (
    <a data-testid={`event-external-${e.id}-link`} href={e.registration_url} target="_blank" rel="noreferrer" className={cls} style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Register <ExternalLink className="w-3.5 h-3.5" /></a>
  );
  if (e.registration_mode === "none") return null;
  if (rsvped) return <Link data-testid={`event-registered-${e.id}-link`} to="/my-temple" className={cls} style={{ background: "var(--gold)", color: "var(--burgundy)" }}><Check className="w-4 h-4" /> Registered</Link>;
  if (e.availability?.full) return <Link data-testid={`event-waitlist-${e.id}-btn`} to={`/gather/${e.id}`} className={cls} style={{ background: "var(--teal)", color: "var(--bg-cream)" }}>Join waitlist</Link>;
  return <Link data-testid={`event-rsvp-${e.id}-btn`} to={`/gather/${e.id}`} className={cls} style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Reserve a seat</Link>;
}

export default function Gather() {
  const { isRsvped } = useApp();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/content/events");
        if (!ignore) setEvents((data || []).filter((e) => !e.cancelled));
      } catch {}
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="mb-10 rise">
        <div className="eyebrow mb-2">Gather</div>
        <h1 className="font-serif text-4xl sm:text-5xl" style={{ color: "var(--burgundy)" }}>Sacred assembly.</h1>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--text-muted)" }}>
          What is discovered alone must eventually be lived together. Circles, retreats, and vigils — places where the interior life becomes shared presence.
        </p>
      </div>

      {loading ? null : events.length === 0 ? (
        <div className="rounded-2xl border p-10 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="gather-empty-state">
          <div className="eyebrow mb-2">Gather</div>
          <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>No gathering is scheduled right now.</p>
          <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>New circles, retreats and vigils are announced here first. Stay connected by email in the footer to hear when the next one opens.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {events.map((e) => {
            const rsvped = isRsvped(e.id);
            const timeStr = e.time || [e.start_time, e.end_time].filter(Boolean).join(" – ");
            return (
              <article key={e.id} className="lift rounded-3xl overflow-hidden border grid md:grid-cols-5 gap-0"
                style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid={`event-card-${e.id}`}>
                <div className="md:col-span-2 relative h-56 md:h-full min-h-[240px]">
                  {e.art_url && <img src={assetUrl(e.art_url)} alt={e.title} className="w-full h-full object-cover" />}
                  <span className="absolute top-4 left-4 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" style={{ background: "rgba(84,28,43,0.9)", color: "var(--gold)" }}>{e.format}</span>
                </div>
                <div className="md:col-span-3 p-6 sm:p-8 flex flex-col">
                  <h2 className="font-serif text-2xl sm:text-3xl leading-tight" style={{ color: "var(--burgundy)" }}>{e.title}</h2>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs" style={{ color: "var(--text-muted)" }}>
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{e.date}{timeStr ? ` · ${timeStr}` : ""}{e.time_zone ? ` ${e.time_zone}` : ""}</span>
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.location}</span>}
                    {(e.availability?.label || e.seats || e.capacity) && <span className="inline-flex items-center gap-1" data-testid={`event-availability-${e.id}`}><Users className="w-3.5 h-3.5" />{e.availability?.label || e.seats || `Capacity ${e.capacity}`}</span>}
                  </div>
                  {e.description && <p className="mt-4 text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--text-obsidian)" }}>{e.description}</p>}
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <EventCta e={e} rsvped={rsvped} />
                    <Link to={`/gather/${e.id}`} data-testid={`event-detail-${e.id}-link`} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Details →</Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
